/**
 * kick-kw (2026-10-23) — 현장별 「킥 키워드」 수집의 순수 층(DB·네트워크 0).
 *
 * 무엇 — 분양예정·청약 현장마다 사람들이 «실제로» 치는 말(「○○ 분양가」「○○ 모델하우스」「에코델타시티 아파트」)을
 *   네이버 검색광고 /keywordstool 로 모아 site_kick_keywords 에 현장 단위로 적는다. 호출·적재는 크론
 *   (app/api/cron/kick-keywords)이 하고, 여기는 «무엇을 묻고 무엇을 어느 현장에 붙이나» 만 판정한다.
 *
 * /keywordstool 의미(tools/naver-sa/sa.py cmd_volume 실측 그대로):
 *   · hintKeywords 는 한 호출 «최대 5개» · 쉼표 구분 → 힌트 안의 쉼표·가운뎃점(·)은 지운다(VOLUME_BAD_CHARS).
 *   · 힌트는 «공백 제거» 형태로 보낸다(API 도 공백 없는 형태로 돌려준다).
 *   · 응답 keywordList 는 «힌트 자신» 과 «연관어» 를 섞어 준다 → 현장 토큰으로 다시 가른다(아래 matchSites).
 *   · monthlyPcQcCnt · monthlyMobileQcCnt 는 10 미만이면 «문자열 "< 10"» — 0 으로 뭉개지 않고 깃발(lt10)로 가른다.
 *   · compIdx 는 「낮음/중간/높음」 문자열 그대로 둔다.
 *
 * ⛔ 교차 오염 금지 — 한 호출에 여러 현장의 힌트가 섞이므로 «현장 핵심 토큰» 을 품은 연관어만 그 현장에 붙인다.
 *    브랜드만 같은 말(「힐스테이트 분양」)은 어느 현장에도 붙이지 않는다. 같은 호출 안 두 현장이 같은 강도로
 *    맞으면(동명 1·2차 등) 어느 쪽에도 붙이지 않는다(모호 → 버림).
 *    지역어(시군구·동·생활권 + 아파트/분양 꼬리)는 modifier='area' 로 그 지역 현장들에 함께 붙인다(공유가 맞다).
 */
import { displayBrandOf, isProvisionalSiteName, officialDisplayName } from '@/lib/apt/provisional-name';
import { LEXICON_WORDS, canonicalModifier } from '@/lib/seo/kw-lexicon';

/* ───────────────────────────── 상수 ───────────────────────────── */

/** /keywordstool hintKeywords 상한(sa.py VOLUME_CHUNK). */
export const KICK_HINTS_PER_CALL = 5;
/** 조회 부적격 문자(sa.py VOLUME_BAD_CHARS — 실측으로 좁힌 집합). */
export const KICK_BAD_CHARS = '·,';
/** 한 현장에 적재할 상한 — 이름계 연관어 / 지역어. 연관어 꼬리(수백 개)를 다 싣지 않는다. */
export const KICK_MAX_NAME_ROWS_PER_SITE = 40;
export const KICK_MAX_AREA_ROWS_PER_SITE = 8;

/**
 * 수집 대상 단계(지시 정본).
 *   kick-cover (2026-10-26) — 공사 중(construction)·미분양(unsold_active)·정비 앞단(plan_approved·union_established) 추가.
 *   종전 6단계(≈230곳)만 돌아 활성 진행 현장 943곳 중 103곳만 측정돼 있었다(공사 중 799곳 통째 제외가 주원인).
 *   늘어난 현장은 «미측정 먼저 · 일정 임박순» 회전 + 주간 재확인(KICK_FRESH_DAYS)으로 할당을 아낀다.
 */
export const KICK_STAGES = [
  'pre_announcement', 'award_pending', 'award_announced', 'subscription_open', 'unsold_active',
  'constructor_selected', 'mgmt_approved', 'plan_approved', 'union_established', 'construction',
] as const;

/** 분양 임박 단계 — 검색량이 빨리 변하니 3일마다, 나머지(공사 중·정비)는 주간 재확인. */
export const KICK_IMMINENT_STAGES: readonly string[] = ['pre_announcement', 'award_pending', 'award_announced', 'subscription_open', 'unsold_active'];
export const KICK_FRESH_DAYS_IMMINENT = 3;
export const KICK_FRESH_DAYS = 7;
/** 재서 «쓸 검색량이 없던» 현장(연관어 0 · 전부 < 10) — 매일 다시 묻지 않고 주간 재확인. */
export const KICK_FRESH_DAYS_EMPTY = 7;

/** 현장 재확인 주기(일) — 단계 · 빈 측정 여부로. */
export function kickFreshDays(stage: string | null | undefined, empty: boolean): number {
  if (empty) return KICK_FRESH_DAYS_EMPTY;
  return KICK_IMMINENT_STAGES.includes(String(stage ?? '')) ? KICK_FRESH_DAYS_IMMINENT : KICK_FRESH_DAYS;
}

/**
 * 확인 장부(app_config seo/kick_kw_checked) — site_kick_keywords 에 행이 안 남는 현장(빈 측정)도 «언제 물었나» 를 남긴다.
 *   종전엔 빈 현장이 «미측정» 으로 보여 매일 앞줄을 먹었다(10/24·25 회차 planned 15~21 중 대부분이 같은 빈 현장 재질의).
 *   at: 마지막 확인 시각 · empty: 그 확인에서 쓸 행이 없었던 현장(값 = 같은 시각).
 */
export interface KickCheckedLedger { v: 1; at: Record<string, string>; empty: Record<string, string> }
export const KICK_LEDGER_NS = 'seo';
export const KICK_LEDGER_KEY = 'kick_kw_checked';
/** 장부 보존 — 이보다 오래된 칸은 지운다(그 사이 비활성화된 현장 정리). */
export const KICK_LEDGER_KEEP_DAYS = 45;

export function parseLedger(v: unknown): KickCheckedLedger {
  const o = (v && typeof v === 'object' ? v : {}) as Partial<KickCheckedLedger>;
  const clean = (m: unknown): Record<string, string> => {
    const out: Record<string, string> = {};
    if (m && typeof m === 'object') for (const [k, x] of Object.entries(m as Record<string, unknown>)) if (typeof x === 'string' && Number.isFinite(Date.parse(x))) out[k] = x;
    return out;
  };
  return { v: 1, at: clean(o.at), empty: clean(o.empty) };
}

/** 이번 회차 결과를 장부에 얹는다(순수). checked = 질의가 성공한 현장 · withRows = 쓸 행이 붙은 현장. */
export function updateLedger(prev: KickCheckedLedger, checked: Iterable<string>, withRows: ReadonlySet<string>, nowIso: string, nowMs = Date.parse(nowIso)): KickCheckedLedger {
  const at = { ...prev.at };
  const empty = { ...prev.empty };
  for (const id of checked) {
    at[id] = nowIso;
    if (withRows.has(id)) delete empty[id]; else empty[id] = nowIso;
  }
  for (const id of withRows) { at[id] = nowIso; delete empty[id]; }
  const keepMs = KICK_LEDGER_KEEP_DAYS * 86_400_000;
  for (const m of [at, empty]) for (const [k, x] of Object.entries(m)) if (nowMs - Date.parse(x) > keepMs) delete m[k];
  return { v: 1, at, empty };
}

/** 마지막 확인 시각 = max(적재 행 checked_at, 장부 at). */
export function mergeLastChecked(rows: ReadonlyMap<string, string>, ledger: KickCheckedLedger): Map<string, string> {
  const out = new Map(rows);
  for (const [k, x] of Object.entries(ledger.at)) { const cur = out.get(k); if (!cur || Date.parse(x) > Date.parse(cur)) out.set(k, x); }
  return out;
}

/**
 * 수식어(꼬리) — 길이 내림으로 맞춘다(「청약일정」이 「청약」보다 먼저).
 * 「견본주택」은 그대로 적고, 선택기에서 「모델하우스」와 한 묶음으로 본다.
 */
const KICK_MODIFIERS_BASE = [
  '청약', '청약일정', '분양가', '모델하우스', '견본주택', '분양일정', '시세', '평면도', '입주', '미분양', '무순위',
  '줍줍', '분양권', '경쟁률', '당첨', '재개발', '재건축', '일반분양', '위치', '학군', '커뮤니티',
] as const;
const BASE_SET: ReadonlySet<string> = new Set(KICK_MODIFIERS_BASE);
/**
 * kw-mine (2026-10-25) — 기존 21개 + 공통 수식어 사전(lib/seo/kw-lexicon 정식어·변형)으로 넓혔다.
 *   기존 21개는 «적힌 그대로» 돌려준다(견본주택·줍줍·당첨 — 이미 적재된 행과 호환). 새 변형은 정식어로 접는다(중도금 → 중도금대출).
 */
export const KICK_MODIFIERS: readonly string[] = [...new Set<string>([...KICK_MODIFIERS_BASE, ...LEXICON_WORDS.filter((w) => w.length >= 2)])];
export type KickModifier = string;
const MODS_BY_LEN = [...KICK_MODIFIERS].sort((a, b) => b.length - a.length);
const asKickModifier = (m: string): KickModifier => (BASE_SET.has(m) ? m : canonicalModifier(m) ?? m);

/** 이름 토큰에서 «변별력 없음» 으로 빼는 일반어. */
const GENERIC_WORDS = new Set([
  '재개발', '재건축', '공동주택', '아파트', '주상복합', '신축공사', '일원', '구역', '단지', '더', 'the', '정비사업', '사업',
  '1단지', '2단지', '3단지', '본청약', '사전청약', '신축', '오피스텔', '도시형생활주택', '주택재개발', '주택재건축',
  '지역주택조합', '주택조합', '가로주택정비', '가로주택', '소규모재건축', '재정비촉진', '민간임대', '공공임대', '공공분양',
]);

/**
 * 브랜드(시공사 상품명) — 이 말 «만» 으로는 현장을 가리키지 못한다(여러 현장에 같은 브랜드).
 * 브랜드 토큰은 지역·고유어 앵커와 «같이» 나와야 그 현장에 붙인다.
 */
export const KICK_BRANDS: readonly string[] = [
  '힐스테이트', '자이', '푸르지오', '롯데캐슬', '더샵', '아이파크', 'e편한세상', '이편한세상', '래미안', '하늘채', '해링턴',
  '포레나', '데시앙', '중흥s클래스', 's클래스', '서희스타힐스', '월드메르디앙', '동원로얄듀크', '비스타동원', '비스타', '수자인',
  '두산위브', '위브', '스위첸', '한신더휴', '더휴', '센트레빌', '우미린', '호반써밋', '써밋', '르엘', '디에이치', '오티에르',
  '아크로', '꿈에그린', '한화포레나', '대방디엠시티', '디엠시티', '노블랜드', '엘크루', '제일풍경채', '풍경채', '반도유보라',
  '유보라', '금강펜테리움', '펜테리움', '쌍용더플래티넘', '더플래티넘', '해모로', '스카이브이', 'sky.v', '시티프라디움', '프라디움',
  '경남아너스빌', '아너스빌', '한양수자인', '이지더원', '코오롱하늘채', '골드클래스', '대광로제비앙', '로제비앙', '한라비발디', '비발디',
  '모아엘가', '엘가', '협성휴포레', '휴포레', '라온프라이빗', '동일스위트', '스위트', '삼정그린코아', '그린코아', '양우내안애', '내안애',
  '신동아파밀리에', '파밀리에', '우방아이유쉘', '아이유쉘', '서한이다음', '이다음', '태영데시앙', '효성해링턴', '롯데', '현대', 'sk뷰', 'sk', '뷰',
];

/** 생활권(시군구·동 이름으로 안 잡히는 권역어). 현장 이름·별칭·동에 이 말이 있으면 그 현장의 지역어가 된다. */
export const KICK_LIVING_AREAS: readonly string[] = [
  '에코델타시티', '에코델타', '명지국제신도시', '명지', '센텀시티', '센텀', '서면', '마린시티', '광안리', '남천', '북항', '정관', '일광',
  '오시리아', '동부산', '다대포', '사직', '온천장', '화명', '덕천', '만덕', '부산대', '송도', '범천', '문현', '감만', '우암', '용호',
  '양정', '초량', '범일', '민락', '재송', '반여', '엄궁', '괴정', '하단', '당리', '신호', '녹산', '대연', '연산',
  '무거', '삼호', '신복', '다운', '송정', '호계', '우정혁신도시', '혁신도시', '문수로', '야음', '태화강', '삼산',
  '물금', '사송', '웅상', '장유', '율하', '삼계', '진영', '창원중앙', '상남', '마산', '진해', '옥포', '고현',
];

const LIVING_SET: ReadonlySet<string> = new Set(KICK_LIVING_AREAS.map((a) => a.toLowerCase()));

/** 지역어 꼬리 — 「{지역}{꼬리}」 꼴만 지역어로 받는다(「서면맛집」 같은 말은 버린다). */
const AREA_SUFFIXES = [
  '', '아파트', '신축아파트', '아파트분양', '분양', '신규분양', '분양예정', '분양정보', '청약', '아파트청약', '시세', '아파트시세',
  '재개발', '재건축', '미분양', '분양권', '모델하우스', '신축', '부동산', '집값', '입주',
];

/* ───────────────────────────── 정규화 ───────────────────────────── */

/** 비교용 정규화 — 공백 제거 · 소문자. */
export function kickNorm(s: string | null | undefined): string {
  return String(s ?? '').replace(/\s+/g, '').toLowerCase();
}

/** 힌트용 — 공백·부적격 문자 제거(원문은 건드리지 않는다). 빈 문자열이면 null. */
export function hintOf(s: string | null | undefined): string | null {
  const n = String(s ?? '').replace(/\s+/g, '');
  const cleaned = [...n].filter((c) => !KICK_BAD_CHARS.includes(c)).join('');
  return cleaned.length >= 2 ? cleaned : null;
}

/* ───────────────────────────── 응답 파싱 ───────────────────────────── */

export interface KwStat {
  /** 공백 제거 형태(API 반환 그대로 정규화) */
  keyword: string;
  pc: number | null;
  mobile: number | null;
  total: number | null;
  lt10: boolean;
  comp: string | null;
}

/** RelKwdStat 한 칸 → (수치, 10미만). "< 10" 은 0 + 깃발 — 「0」과 「1~9」를 섞지 않는다(sa.py _vol_num). */
export function parseVolume(x: unknown): { n: number | null; lt10: boolean } {
  if (typeof x === 'boolean') return { n: null, lt10: false };
  if (typeof x === 'number') return Number.isFinite(x) ? { n: Math.round(x), lt10: false } : { n: null, lt10: false };
  const s = String(x ?? '').trim().replace(/,/g, '');
  if (!s) return { n: null, lt10: false };
  if (s.startsWith('<')) return { n: 0, lt10: true };
  const n = Number(s);
  return Number.isFinite(n) ? { n: Math.round(n), lt10: false } : { n: null, lt10: false };
}

/** /keywordstool 응답 본문 → 행. parsed=false 는 「JSON 이 아니다/keywordList 없음」(무행과 다르다). */
export function parseKeywordsTool(body: string): { rows: KwStat[]; parsed: boolean } {
  let j: { keywordList?: unknown };
  try { j = JSON.parse(body); } catch { return { rows: [], parsed: false }; }
  if (!Array.isArray(j?.keywordList)) return { rows: [], parsed: false };
  const out = new Map<string, KwStat>();
  for (const r of j.keywordList as Array<Record<string, unknown>>) {
    const kw = String(r?.relKeyword ?? '').replace(/\s+/g, '');
    if (!kw) continue;
    const pc = parseVolume(r.monthlyPcQcCnt);
    const mo = parseVolume(r.monthlyMobileQcCnt);
    const total = pc.n == null && mo.n == null ? null : (pc.n ?? 0) + (mo.n ?? 0);
    const comp = typeof r.compIdx === 'string' && r.compIdx.trim() ? r.compIdx.trim() : null;
    if (!out.has(kw)) out.set(kw, { keyword: kw, pc: pc.n, mobile: mo.n, total, lt10: pc.lt10 || mo.lt10, comp });
  }
  return { rows: [...out.values()], parsed: true };
}

/* ───────────────────────────── 수식어 ───────────────────────────── */

/**
 * 수식어 판정 — 현장 이름 부분(strip)을 먼저 떼고 남은 꼬리에서 찾는다
 * (「문현3재개발」 자체가 이름인데 「재개발」 수식어로 오인하지 않게).
 *   ① 꼬리가 수식어로 «끝나면» 그것(길이 내림) ② 꼬리 «안에» 있으면 그것 ③ 없으면 null.
 *   「입주자모집공고」는 청약 의도라 '청약'.
 */
export function deriveModifier(keyword: string, strip: readonly string[] = [], extra: readonly string[] = []): KickModifier | null {
  let rest = kickNorm(keyword);
  const heads = [...strip].map(kickNorm).filter((s) => s.length >= 2).sort((a, b) => b.length - a.length);
  for (const h of heads) {
    const i = rest.indexOf(h);
    if (i >= 0) { rest = rest.slice(0, i) + rest.slice(i + h.length); break; }
  }
  if (!rest) return null;
  if (rest.includes('모집공고')) return '청약';
  for (const m of MODS_BY_LEN) if (rest.endsWith(m)) return asKickModifier(m);
  for (const m of MODS_BY_LEN) if (rest.includes(m)) return asKickModifier(m);
  // 채굴 발견 수식어(v_keyword_modifiers — 사전 밖) — 꼬리가 «정확히» 그 말일 때만(측정·보고 전용 · 선택기 단계 목록 밖이라 페이지 노출 0).
  const ex = extra.map(kickNorm).filter((x) => x.length >= 2);
  if (ex.includes(rest)) return rest;
  return null;
}

/* ───────────────────────────── 현장 이름·토큰 ───────────────────────────── */

export interface KickSiteInput {
  id: string;
  name: string;
  display_name?: string | null;
  name_variants?: unknown;
  region?: string | null;
  sigungu?: string | null;
  dong?: string | null;
  lifecycle_stage?: string | null;
}

function stemOf(word: string, suffixRe: RegExp, minLen = 2): string | null {
  const s = word.replace(suffixRe, '');
  return s !== word && s.length >= minLen ? s : null;
}

/** 현장 이름 앞머리에서 뗄 지역어(시도·시군구·동과 그 어간). */
export function localityWordsOf(site: Pick<KickSiteInput, 'region' | 'sigungu' | 'dong'>): string[] {
  const out = new Set<string>();
  const add = (w: string | null | undefined) => { const n = kickNorm(w); if (n) out.add(n); };
  const region = String(site.region ?? '').trim();
  add(region);
  if (region) { add(`${region}시`); add(`${region}광역시`); }
  const sgg = String(site.sigungu ?? '').trim();
  add(sgg);
  if (sgg) add(stemOf(sgg, /(구|시|군)$/));
  const dong = String(site.dong ?? '').trim();
  add(dong);
  if (dong) add(stemOf(dong, /(동|리|읍|면|가)$/));
  return [...out];
}

function isBrandToken(tok: string): boolean {
  const t = kickNorm(tok);
  return KICK_BRANDS.some((b) => t === b || (t.includes(b) && t.length - b.length <= 2));
}

/** 괄호 꼬리 제거 — 「감만 푸르지오 센트레빌(감만1구역)」 → 「감만 푸르지오 센트레빌」 · 「…(조합원취소분)(2회차)」 → 「…」. */
export function stripParens(s: string | null | undefined): string {
  return String(s ?? '').replace(/[（(][^）)]*[）)]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** 괄호 속 구역명(「(감만1구역)」「(시민공원 촉진2-1구역)」) — 정비 별칭 후보. */
export function parenZoneOf(s: string | null | undefined): string | null {
  for (const m of String(s ?? '').matchAll(/[（(]([^）)]*구역)[）)]/g)) {
    const z = m[1].replace(/\s+/g, ' ').trim();
    if (z) return z;
  }
  return null;
}

/**
 * 검색에 쓰는 이름(공백 유지 · 괄호 꼬리 제거) — 가칭이 아닌 첫 후보:
 *   정식명(「정식 — 가칭」 왼쪽) → 표시명 브랜드축 → name → 표시명. 전부 가칭이면 null.
 *   ⚠️ name 을 표시명보다 앞에 둔다 — 정비 표시명은 「부산 남구 문현3 재개발」처럼 지역을 붙인 표시용이고,
 *      분양 현장 name 은 대개 공고 단지명(사람이 치는 말)이다.
 */
export function kickNameOf(site: Pick<KickSiteInput, 'name' | 'display_name'>): string | null {
  const cands = [officialDisplayName(site), displayBrandOf(site.display_name), site.name, site.display_name];
  for (const c of cands) {
    const n = stripParens(c);
    if (n && !isProvisionalSiteName(n)) return n; // 괄호를 뗀 뒤 판정(「…(19BL)」 꼬리 때문에 정식명을 버리지 않는다)
  }
  return null;
}

/** 이름 다음으로 쓸 «다른 이름» — 표시명·괄호 구역명 중 이름과 다른 것(정비 브랜드 ↔ 구역명 쌍). */
function altNamesOf(site: KickSiteInput, primary: string): string[] {
  const p = kickNorm(primary);
  const out: string[] = [];
  for (const c of [stripParens(displayBrandOf(site.display_name) ?? site.display_name), parenZoneOf(site.name), stripParens(site.name)]) {
    const n = String(c ?? '').trim();
    if (!n || isProvisionalSiteName(n)) continue;
    const k = kickNorm(n);
    if (k === p || p.includes(k) || k.includes(p)) continue;
    if (!out.some((o) => kickNorm(o) === k)) out.push(n);
  }
  return out;
}

export interface KickSiteProfile {
  siteId: string;
  /** 검색 이름(공백 유지, 지역 앞머리 포함 원형) */
  name: string;
  /** 지역 앞머리를 뗀 핵심(공백 유지) */
  core: string;
  /** 힌트(공백 제거, ≤2) */
  hints: string[];
  /** 강한 토큰 — 하나만 품어도 그 현장 */
  strong: string[];
  /** 이름급 토큰(힌트·핵심) — 같은 길이로 맞으면 «이름 전체» 로 맞은 현장이 이긴다 */
  names: string[];
  /** 브랜드 토큰 — 앵커와 같이 나와야 */
  brands: string[];
  /** 앵커 — 동·이름 속 지역어·고유어 */
  anchors: string[];
  /** 지역어 토큰(area 판정) */
  areas: string[];
  /** 지역어 앞에 붙어도 되는 접두(시도·시군구 어간) */
  areaPrefixes: string[];
}

/** 정비 이름 「개금2 재개발」 → 「개금2구역」 · 「문현3구역 재개발」 → 「문현3구역」. 아니면 null. */
export function zoneAliasOf(core: string): string | null {
  const m = /^(.+?)\s*(재개발|재건축|정비사업)$/.exec(core.trim());
  if (!m) return null;
  const head = m[1].replace(/\s+/g, '');
  if (!/\d/.test(head) && !head.endsWith('구역')) return null;
  return head.endsWith('구역') ? head : `${head}구역`;
}

/** 현장의 생활권·시군구·동 지역어. */
export function areaTokensOf(site: KickSiteInput): { areas: string[]; prefixes: string[] } {
  const areas = new Set<string>();
  const prefixes = new Set<string>();
  const region = kickNorm(site.region);
  if (region) prefixes.add(region);
  const sgg = String(site.sigungu ?? '').trim();
  const sggN = kickNorm(sgg);
  const sggStem = sgg ? stemOf(sggN, /(구|시|군)$/) : null;
  if (sggN.length >= 3) areas.add(sggN);
  if (sggStem && sggStem.length >= 3) areas.add(sggStem);
  if (sggN && sggN.length < 3 && region) areas.add(`${region}${sggN}`); // 「남구」 → 「부산남구」
  if (sggStem) prefixes.add(sggStem);
  if (sggN.endsWith('시') && sggStem) areas.add(sggStem); // 「김해시」 → 「김해」
  const dongN = kickNorm(site.dong);
  if (dongN.length >= 3) areas.add(dongN);
  const text = kickNorm([site.name, site.display_name, site.dong, ...(Array.isArray(site.name_variants) ? site.name_variants : [])].join(' '));
  for (const a of KICK_LIVING_AREAS) if (text.includes(kickNorm(a))) areas.add(kickNorm(a));
  return { areas: [...areas], prefixes: [...prefixes] };
}

/** 변별 토큰 — 한글을 품고, 일반어·지역어·생활권어·브랜드가 아니며, 「2차」「3단지」 같은 차수 꼬리가 아닌 말. */
function isDistinctToken(w: string, loc: ReadonlySet<string>): boolean {
  if (w.length < 2 || GENERIC_WORDS.has(w) || loc.has(w) || LIVING_SET.has(w) || isBrandToken(w)) return false;
  if (!/[가-힣]/.test(w)) return false;
  if (/^\d+(차|단지|회차|bl|블록)$/.test(w)) return false;
  return true;
}

/** 이름 하나 → (핵심, 지역 앞머리, 변별 여부). 앞머리는 «뒤에 변별어가 남을 때만» 뗀다(「울산 중구 학성동 재개발」 → 「학성동 재개발」). */
function splitCore(name: string, loc: ReadonlySet<string>): { core: string; stripped: string[]; words: string[] } {
  const words = name.split(' ').filter(Boolean);
  const stripped: string[] = [];
  const restHasSubstance = (ws: string[]) => ws.some((w) => { const n = kickNorm(w); return n.length >= 2 && !GENERIC_WORDS.has(n); });
  while (words.length > 1 && loc.has(kickNorm(words[0])) && restHasSubstance(words.slice(1))) stripped.push(kickNorm(words.shift()!));
  return { core: words.join(' '), stripped, words: words.map(kickNorm) };
}

/**
 * 현장 → 힌트·토큰 프로필. 검색 이름이 없거나(가칭) 핵심이 너무 짧으면 null.
 *   힌트: ① 이름(공백 제거) ② 짧은 별칭 — 정비면 「○○N구역」, 아니면 지역 앞머리를 뗀 핵심, 같으면 별칭표에서 하나
 *        ③ (있으면) 다른 이름 — 정비 브랜드 ↔ 구역명 쌍(「디에이치 아센테르」 ↔ 「우동3구역」). 최대 3.
 *   ⚠️ 핵심에 변별어가 없으면(「울산 남구 B-04 재개발」 → 「B-04 재개발」은 중구 B-04 와 같다) 핵심·별칭을 힌트/강토큰으로
 *      쓰지 않고 «지역을 품은 전체 이름» 만 쓴다.
 */
export function buildSiteProfile(site: KickSiteInput): KickSiteProfile | null {
  const name = kickNameOf(site);
  if (!name) return null;
  const loc = new Set(localityWordsOf(site));
  const { core, stripped, words } = splitCore(name, loc);
  const coreN = kickNorm(core);
  if (coreN.length < 3) return null;
  if (!words.some((w) => w.length >= 2 && !GENERIC_WORDS.has(w))) return null;
  const parenZone = parenZoneOf(site.name);
  // 괄호 구역명(「(감만1구역)」)이 있으면 브랜드만으로 된 이름이어도 변별된다(구역명이 고유).
  // 변별 = 고유어가 있거나, 브랜드 + 장소어(「서면 서희스타힐스」)처럼 둘이 짝을 이룬 핵심. 브랜드만(「푸르지오 센트레빌」)·
  // 지역+일반어만(「B-04 재개발」)은 변별이 아니다.
  const meaningful = words.filter((w) => w.length >= 2 && !GENERIC_WORDS.has(w));
  const coreDistinct = words.some((w) => isDistinctToken(w, loc))
    || (meaningful.some(isBrandToken) && meaningful.some((w) => !isBrandToken(w) && /[가-힣]/.test(w)));
  const distinct = coreDistinct || !!parenZone;

  const hint1 = hintOf(name);
  const zone = zoneAliasOf(core) ?? parenZone;
  let alias: string | null = distinct ? hintOf(zone ?? core) : null;
  if (distinct && alias && alias === hint1) {
    // 핵심 = 이름이면 별칭표에서 짧고 지역어 순열이 아닌 것 하나.
    const vars = (Array.isArray(site.name_variants) ? site.name_variants : []).map((v) => String(v ?? ''));
    const pick = vars
      .map((v) => ({ v, n: hintOf(stripParens(v)) }))
      .filter((x) => x.n && x.n !== hint1 && x.n.length >= 4 && !isProvisionalSiteName(x.v) && ![...loc].some((l) => l.length >= 2 && kickNorm(x.v).startsWith(l)))
      .sort((a, b) => a.n!.length - b.n!.length)[0];
    alias = pick?.n ?? null;
  }
  // 다른 이름이 같은 구역의 표시형(「부산 감만1 재개발」 ↔ 구역명 「감만1구역」)이면 버린다 — 별칭이 이미 그 말이다.
  const alts = altNamesOf(site, name).filter((a) => {
    if (kickNorm(zone ?? '').includes(kickNorm(a))) return false;
    const az = zoneAliasOf(splitCore(a, loc).core);
    return !(az && zone && kickNorm(az) === kickNorm(zone));
  });
  const alt = alts.find((a) => splitCore(a, loc).words.some((w) => isDistinctToken(w, loc)));
  const hints = [...new Set([hint1, alias, alt ? hintOf(alt) : null].filter((h): h is string => !!h))].slice(0, 3);

  const strong = new Set<string>(hints.map(kickNorm));
  // 핵심이 브랜드만(「푸르지오 센트레빌」)이면 핵심은 강토큰이 아니다 — 여러 현장 공유.
  if (coreDistinct) strong.add(coreN);
  if (distinct && zone) strong.add(kickNorm(zone));
  const brands = new Set<string>();
  const anchors = new Set<string>(stripped.filter((s) => s !== kickNorm(site.region)));
  const allWords = [...words, ...(alt ? splitCore(alt, loc).words : [])];
  for (const w of allWords) {
    if (w.length < 2 || GENERIC_WORDS.has(w) || /^\d+(차|단지|회차)$/.test(w)) continue; // 「2차」 같은 차수는 앵커도 아니다
    if (isBrandToken(w)) brands.add(w);
    else if (isDistinctToken(w, loc) && (w.length >= 4 || /\d/.test(w))) strong.add(w);
    else anchors.add(w); // 지역어·생활권어(에코델타시티 등 여러 현장 공유)·짧은 말 → 앵커
  }
  const dongN = kickNorm(site.dong);
  if (dongN.length >= 2) anchors.add(dongN);
  const dongStem = stemOf(dongN, /(동|리|읍|면|가)$/);
  if (dongStem) anchors.add(dongStem);

  const { areas, prefixes } = areaTokensOf(site);
  return {
    siteId: site.id, name, core, hints,
    strong: [...strong].filter((s) => s.length >= 3),
    names: [...new Set([...hints.map(kickNorm), ...(coreDistinct ? [coreN] : [])])],
    brands: [...brands], anchors: [...anchors].filter((a) => a.length >= 2),
    areas, areaPrefixes: prefixes,
  };
}

/* ───────────────────────────── 매칭 ───────────────────────────── */

/** 이름계 매칭 강도 — 2 강함(고유 토큰) · 1 약함(브랜드+앵커) · 0 없음. */
export function nameMatchScore(keyword: string, p: KickSiteProfile): 0 | 1 | 2 {
  return nameMatch(keyword, p).score;
}

/** 강도 + 맞은 길이(강함: 가장 긴 강토큰 · 약함: 브랜드+앵커 길이) — 「트리븐센텀」 vs 「트리븐센텀2단지」 를 길이로 가른다. */
export function nameMatch(keyword: string, p: KickSiteProfile): { score: 0 | 1 | 2; len: number; exact: boolean } {
  const k = kickNorm(keyword);
  const s = p.strong.filter((t) => k.includes(t));
  if (s.length) {
    const len = Math.max(...s.map((t) => t.length));
    if (foreignResidue(k, p)) return { score: 0, len: 0, exact: false };
    // 키워드가 «토큰 하나뿐»(수식어 떼고)이고 그 토큰이 현장 이름 전체가 아니면 — 「더리브」「지역주택조합」 같은 브랜드·일반어 단독 — 붙이지 않는다(2026-10-23 실측 오배정).
    const bare = MODS_BY_LEN.reduce((acc, m) => (acc.endsWith(m) ? acc.slice(0, -m.length) : acc), k);
    if (s.includes(bare) && (GENERIC_WORDS.has(bare) || (bare.length <= 3 && bare !== kickNorm(p.hints[0] ?? '')))) return { score: 0, len: 0, exact: false };
    return { score: 2, len, exact: s.some((t) => t.length === len && p.names.includes(t)) };
  }
  const b = p.brands.filter((t) => k.includes(t));
  // 약한 매칭의 앵커는 3자 이상 또는 키워드 맨 앞 — 「양정자이…」의 「정자」(정자동 어간) 같은 우연 포함을 막는다(2026-10-23 실측 오배정).
  const a = p.anchors.filter((t) => k.includes(t) && (t.length >= 3 || k.startsWith(t)) && !p.areas.includes(t));
  if (b.length && a.length && !foreignResidue(k, p)) return { score: 1, len: Math.max(...b.map((t) => t.length)) + Math.max(...a.map((t) => t.length)), exact: false };
  return { score: 0, len: 0, exact: false };
}

/** 이 현장 말로 설명되지 않는 «남는 말». 이름 토큰·앵커·브랜드·지역·수식어·일반어를 지우고 2자 이상 남으면 다른 단지 이름이다
 *   (2026-10-23 첫 적재 실측: 「청라더리브티아모」→울산 달동 더리브, 「사직2구역」→영통 2구역, 「과천디에트르퍼스티지」→영종 디에트르,
 *    「두산위브더제니스오션시티」→부천, 「미아4재정비촉진구역」→대구 신암4). */
const RESIDUE_GENERIC = ['아파트', '분양', '공식', '홈페이지', '사이트', '조감도', '평형', '가격', '주택', '정비사업', '정비', '사업', '단지', '구역', '지구', '재정비촉진', '촉진'];
export function foreignResidue(k: string, p: KickSiteProfile): boolean {
  let r = k;
  const parts = [...p.names, ...p.strong, ...p.hints.map(kickNorm), ...p.anchors, ...p.brands, ...p.areas, ...p.areaPrefixes, ...MODS_BY_LEN, ...RESIDUE_GENERIC]
    .filter((x) => x && x.length >= 1)
    .sort((a, b) => b.length - a.length);
  for (const t of parts) r = r.split(t).join(' ');
  return r.split(' ').some((x) => x.trim().length >= 2);
}

/** 지역어인가 — 「[접두]{지역}{꼬리}」 꼴(꼬리는 AREA_SUFFIXES). */
export function isAreaKeyword(keyword: string, p: Pick<KickSiteProfile, 'areas' | 'areaPrefixes'>): boolean {
  const k = kickNorm(keyword);
  for (const a of p.areas) {
    const i = k.indexOf(a);
    if (i < 0) continue;
    const head = k.slice(0, i);
    const tail = k.slice(i + a.length);
    if (head && !p.areaPrefixes.includes(head)) continue;
    if (AREA_SUFFIXES.includes(tail)) return true;
  }
  return false;
}

export interface KickUpsertRow {
  site_id: string;
  keyword: string;
  modifier: string | null;
  volume_pc: number | null;
  volume_mobile: number | null;
  volume_total: number | null;
  comp_idx: string | null;
  is_lt10: boolean;
  source: string;
  checked_at: string;
}

/**
 * 한 호출의 응답 행 → 현장별 적재 행.
 *   이름계: 비교 대상(universe — 기본 이 호출의 현장들, 크론은 «전체 후보» 를 넘긴다) 중 (강도, 맞은 길이) 최고가
 *          유일할 때만 그 현장(모호 = 버림, ambiguous 로 센다). 전체를 넘기면 다른 회차에서 물은 현장의 말
 *          (「트리븐센텀2단지…」를 「트리븐 센텀」에)도 가로채지 않는다. 이긴 현장이 이 호출 밖이어도 그 현장에 적는다.
 *   지역어: 이름계로 안 붙은 말 중 지역어 꼴이면 해당 지역 현장 «모두» 에 modifier='area'.
 *   현장별 상한: 이름계 KICK_MAX_NAME_ROWS_PER_SITE · 지역어 KICK_MAX_AREA_ROWS_PER_SITE (검색량 내림).
 */
export function assignRows(
  stats: readonly KwStat[],
  profiles: readonly KickSiteProfile[],
  checkedAt: string,
  universe: readonly KickSiteProfile[] = profiles,
  extraModifiers: readonly string[] = [],
): { rows: KickUpsertRow[]; ambiguous: number; dropped: number } {
  const name = new Map<string, KickUpsertRow[]>();
  const area = new Map<string, KickUpsertRow[]>();
  let ambiguous = 0;
  let dropped = 0;
  const mk = (p: KickSiteProfile, s: KwStat, modifier: string | null): KickUpsertRow => ({
    site_id: p.siteId, keyword: s.keyword, modifier,
    volume_pc: s.pc, volume_mobile: s.mobile, volume_total: s.total, comp_idx: s.comp, is_lt10: s.lt10,
    source: 'searchad_keywordstool', checked_at: checkedAt,
  });
  for (const s of stats) {
    const scored = universe.map((p) => ({ p, m: nameMatch(s.keyword, p) })).filter((x) => x.m.score > 0);
    if (scored.length) {
      const top = Math.max(...scored.map((x) => x.m.score));
      const topLen = Math.max(...scored.filter((x) => x.m.score === top).map((x) => x.m.len));
      let best = scored.filter((x) => x.m.score === top && x.m.len === topLen);
      // 같은 길이면 «이름 전체» 로 맞은 쪽(「트리븐센텀분양가」 → 「트리븐 센텀」, 「트리븐센텀 2단지」의 낱말 맞음보다 앞).
      if (best.length > 1 && best.some((x) => x.m.exact)) best = best.filter((x) => x.m.exact);
      if (best.length > 1) { ambiguous++; continue; }
      const p = best[0].p;
      const strip = [...p.strong, ...p.hints, p.core, p.name, ...p.anchors, ...p.brands];
      const row = mk(p, s, deriveModifier(s.keyword, strip, extraModifiers));
      const l = name.get(p.siteId); if (l) l.push(row); else name.set(p.siteId, [row]);
      continue;
    }
    const areaSites = profiles.filter((p) => isAreaKeyword(s.keyword, p));
    if (!areaSites.length) { dropped++; continue; }
    for (const p of areaSites) {
      const row = mk(p, s, 'area');
      const l = area.get(p.siteId); if (l) l.push(row); else area.set(p.siteId, [row]);
    }
  }
  const byVol = (a: KickUpsertRow, b: KickUpsertRow) => (b.volume_total ?? -1) - (a.volume_total ?? -1);
  const rows: KickUpsertRow[] = [];
  for (const l of name.values()) rows.push(...l.sort(byVol).slice(0, KICK_MAX_NAME_ROWS_PER_SITE));
  for (const l of area.values()) rows.push(...l.sort(byVol).slice(0, KICK_MAX_AREA_ROWS_PER_SITE));
  return { rows, ambiguous, dropped };
}

/* ───────────────────────────── 배치·회전 ───────────────────────────── */

/** 현장 힌트를 «한 현장의 힌트는 한 호출에» 묶어 호출 단위로 자른다(≤5). */
export function batchProfiles(profiles: readonly KickSiteProfile[], perCall = KICK_HINTS_PER_CALL): KickSiteProfile[][] {
  const out: KickSiteProfile[][] = [];
  let cur: KickSiteProfile[] = [];
  let n = 0;
  for (const p of profiles) {
    const h = Math.min(p.hints.length, perCall);
    if (!h) continue;
    if (cur.length && n + h > perCall) { out.push(cur); cur = []; n = 0; }
    cur.push(p); n += h;
  }
  if (cur.length) out.push(cur);
  return out;
}

/** 호출 한 번의 힌트(중복 제거 · ≤5). */
export function hintsOfBatch(batch: readonly KickSiteProfile[]): string[] {
  return [...new Set(batch.flatMap((p) => p.hints))].slice(0, KICK_HINTS_PER_CALL);
}

/**
 * 회전 계획 — 신선(freshDays 이내 확인 · freshDaysOf 를 주면 현장별 주기)한 현장은 뺀다. 남은 것:
 *   ① 한 번도 안 잰 현장 → ② 오래된 순(checked_at 오름) 이 «먼저», 같은 층 안에서는 입력 순서(=일정 임박순)가 먼저, 부울경은 동점 기준(national-preempt).
 *   ⚠️ 입력은 이미 일정 임박순(sortBySchedule)으로 줄 세워 와야 한다.
 *   «오래된 순» 은 하루 단위로 뭉갠다 — 같은 날 잰 현장끼리는 일정 순서가 이긴다.
 */
export function planRotation<T extends { id: string; region?: string | null }>(
  ordered: readonly T[],
  lastChecked: ReadonlyMap<string, string>,
  opts: { nowMs: number; freshDays?: number; max?: number; freshDaysOf?: (t: T) => number },
): T[] {
  const max = opts.max ?? 120;
  const rows = ordered.map((t, i) => {
    const at = lastChecked.get(t.id);
    const ms = at ? Date.parse(at) : NaN;
    return { t, i, ms, day: Number.isFinite(ms) ? Math.floor(ms / 86_400_000) : -1 };
  }).filter((x) => {
    const freshMs = (opts.freshDaysOf ? opts.freshDaysOf(x.t) : (opts.freshDays ?? 3)) * 86_400_000;
    return !(Number.isFinite(x.ms) && opts.nowMs - x.ms < freshMs);
  });
  // national-preempt (2026-10-26) — 같은 층 안에서는 입력 순서(=일정 임박순). 레인 K — 지역 동점 기준(부울경) 제거(입력 순서가 유일해 쓰인 적 없음).
  rows.sort((a, b) => a.day - b.day || a.i - b.i);
  return rows.slice(0, max).map((x) => x.t);
}
